// The recorder's snapshot: what screens, the recording bar and the Mac app
// read, built from the engine's takes in one pure step (recorderCore.ts
// calls it on every change).

import type { Meeting } from "@/api/meetings";
import { shellPhaseOf, type ShellPhase } from "./recorderModel";
import type { TakeRecord } from "./recorderStore";
import type { Runtime, Waiting } from "./recorderTypes";
import type { CallSoundState, CallVia } from "./sourceModel";

export type RecorderPhase =
  | "idle" | "requesting" | "recording" | "stopping" | "uploading" | "transcribing" | "ready" | "failed";

/** "Carry on?": a take that stopped without Stop being pressed. */
export interface CarryOnOffer {
  takeId: string;
  /** Whose take it is (its workspace and who recorded it): only that sign-in is offered it. */
  customerId: string;
  owner?: string;
  /** The meeting a carry-on goes into (null until the server has filed the take). */
  meetingId: string | null;
  title: string;
  at: number;
  reason: string;
  savedSec: number;
  /** Cut short and waiting for an answer: finishes on its own at this time. Null: already finishing. */
  finishAt: number | null;
  /** Its parts still on this device, not yet on the server (in the snapshot). */
  unsent?: number;
  /** A call recording: Carry on shares the call again. */
  callSound?: boolean;
}

/**
 * A finished take kept here to save as a file: parts Corvus would not add
 * (part_exists), or audio Corvus found none in (empty_recording, `noAudio`).
 */
export interface KeptAudio {
  takeId: string;
  title: string;
  parts: number;
  noAudio: boolean;
}

export interface RecorderSnapshot {
  phase: RecorderPhase;
  /** The microphone is being asked for, or is live (a new recording cannot start). */
  capturing: boolean;
  meeting: Meeting | null;
  title: string | null;
  startedAt: number | null;
  elapsedSec: number;
  level: number;
  quietSec: number;
  muted: boolean;
  bytesSent: number;
  unsent: number;
  /** Earlier recordings of this account still uploading beside the one shown. */
  pending: number;
  /**
   * This account's takes cut short and waiting for Carry on / Finish now: the held
   * ones, and one whose microphone stopped by itself while "Carry on?" is still asked.
   */
  held: number;
  /** Takes in this device's store, every account (null until looked, or with no device storage). */
  stored: number | null;
  /** The store has been looked in once (the Mac app is told nothing before). */
  scanned: boolean;
  clientRef: string | null;
  meetingId: string | null;
  registered: boolean;
  waiting: Waiting;
  refusal: string | null;
  storage: "device" | "memory";
  error: string | null;
  notice: string | null;
  offer: CarryOnOffer | null;
  kept: KeptAudio[];
  shellPhase: ShellPhase;
  /** A screen of Corvus is bound (signed in); false while a recording runs on behind the sign-in page. */
  bound: boolean;
  /** Every unfinished take of this sign-in the store held at the last look has been taken over by this page. */
  adopted: boolean;
  /** Flushes (the Mac app's) whose slice is written to the device. */
  flushed: number;
  /** A call recording live: what it still hears (null: the microphone alone, or nothing live). */
  callSound: CallSoundState | null;
  /** A call recording live whose call side is silent while the microphone hears speech. */
  callSilent: boolean;
  /** A call recording live: where its call side comes from (a share, or the Mac app's own capture). */
  callVia: CallVia | null;
  /** A call recording live whose call side is gone because the Mac stopped allowing it. */
  callLost: "denied" | null;
}

export const IDLE: RecorderSnapshot = {
  phase: "idle", capturing: false, meeting: null, title: null, startedAt: null, elapsedSec: 0, level: 0, quietSec: 0,
  muted: false, bytesSent: 0, unsent: 0, pending: 0, held: 0, stored: null, scanned: false, clientRef: null,
  meetingId: null, registered: false, waiting: "none", refusal: null, storage: "device", error: null, notice: null,
  offer: null, kept: [], shellPhase: "idle", bound: false, adopted: false, flushed: 0, callSound: null, callSilent: false,
  callVia: null, callLost: null,
};

/** Which wait the screen names when several takes wait: the one the person must act on first. */
const WORST: Waiting[] = ["refused", "storage_full", "signed_out", "offline", "none"];

export interface SnapshotInput {
  now: number;
  /** The live microphone's take, whoever's it is (the microphone is this page's). */
  live: Runtime | null;
  /** The take shown: this sign-in's live take, else its closing one. */
  cur: Runtime | null;
  /** This sign-in's takes still in hand. */
  mine: Runtime[];
  rts: Map<string, Runtime>;
  unsentOf: (rt: Runtime) => number;
  titleOf: (rt: Runtime) => string;
  after: Meeting | null;
  requesting: boolean;
  failure: string | null;
  notice: string | null;
  offer: CarryOnOffer | null;
  kept: TakeRecord[];
  meetings: Map<string, Meeting>;
  level: number;
  lastLoudAt: number;
  storedCount: number | null;
  scanned: boolean;
  storage: "device" | "memory";
  bound: boolean;
  adopted: boolean;
  flushed: number;
  callSound: CallSoundState | null;
  callSilent?: boolean;
  callVia?: CallVia | null;
  callLost?: "denied" | null;
}

/** When a take's run began: a carry-on goes on from the first take's start (the Mac app's clock). */
const startOf = (rt: Runtime) => rt.rec.chainStartedAt ?? rt.rec.startedAt;

export function snapshotOf(i: SnapshotInput): RecorderSnapshot {
  const { now, live, cur, mine, after } = i;
  const closing = cur && cur !== live ? cur : null;
  let unsent = 0;
  let worst: Runtime | null = null;
  for (const rt of mine) {
    unsent += i.unsentOf(rt);
    if (WORST.indexOf(rt.waiting) < WORST.indexOf(worst?.waiting ?? "none")) worst = rt;
  }
  const held = mine.filter((rt) => rt.rec.status === "held");
  const finishing = mine.some((rt) => rt.rec.status === "closing" || (rt.rec.status === "held" && rt.rec.finishAt !== null && now >= rt.rec.finishAt));
  const latestHeld = [...held].sort((a, b) => b.rec.startedAt - a.rec.startedAt)[0] ?? null;
  const latest = cur ?? latestHeld;
  // "Carry on?" after the microphone stopped by itself (its take already finishing): cut short and waiting too (D1).
  const offerHeld = i.offer && i.offer.finishAt === null ? 1 : 0;
  const phase: RecorderPhase = i.requesting
    ? "requesting"
    : live
      ? live.stopping ? "stopping" : "recording"
      : closing
        ? "uploading"
        : after
          ? after.recording_status === "ready" ? "ready" : after.recording_status === "failed" ? "failed" : "transcribing"
          : i.failure ? "failed" : "idle";
  const offerRt = i.offer ? i.rts.get(i.offer.takeId) : undefined;
  return {
    phase,
    capturing: i.requesting || !!live,
    meeting: cur ? (i.meetings.get(cur.rec.id) ?? null) : after,
    title: latest ? i.titleOf(latest) : (i.offer?.title ?? after?.title ?? null),
    startedAt: cur ? startOf(cur) : null,
    elapsedSec: live ? Math.max(0, Math.round((now - live.rec.startedAt) / 1000)) : Math.round(cur?.rec.audioSec ?? after?.recording_duration_sec ?? 0),
    level: live ? i.level : 0,
    quietSec: live ? Math.max(0, Math.round((now - Math.max(i.lastLoudAt, live.rec.startedAt)) / 1000)) : 0,
    muted: !!live?.mutedSince,
    bytesSent: cur?.bytesSent ?? 0,
    unsent,
    pending: mine.filter((rt) => rt !== cur && rt.rec.status === "closing").length,
    held: held.length + offerHeld,
    stored: i.storage === "memory" ? null : i.storedCount,
    scanned: i.scanned,
    clientRef: latest?.rec.id ?? null,
    meetingId: latest?.rec.meetingId ?? null,
    registered: !!cur?.rec.upload,
    // The take the person must act on first, whichever it is (one carried over from a closed page too).
    waiting: worst?.waiting ?? cur?.waiting ?? "none",
    refusal: worst?.refusal ?? cur?.refusal ?? null,
    storage: i.storage,
    error: phase === "failed" ? (after?.recording_error ?? i.failure ?? "Something went wrong.") : null,
    notice: i.notice,
    offer: i.offer ? { ...i.offer, unsent: offerRt ? i.unsentOf(offerRt) : 0 } : null,
    kept: i.kept.map((t) => ({
      takeId: t.id, title: t.meetingTitle || t.title || "the meeting", parts: t.noAudio ? t.nextSeq : (t.keptParts?.length ?? 0),
      noAudio: !!t.noAudio,
    })),
    shellPhase: shellPhaseOf({ starting: i.requesting, live: !!live, unsent, finishing }),
    bound: i.bound,
    adopted: i.adopted,
    flushed: i.flushed,
    callSound: live ? i.callSound : null,
    callSilent: live ? !!i.callSilent : false,
    callVia: live ? (i.callVia ?? null) : null,
    callLost: live ? (i.callLost ?? null) : null,
  };
}
